import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { render } from 'react-dom'
import { useHotkeys } from 'react-hotkeys-hook'
import { ButtonBase } from '@material-ui/core'
import { Subscription } from 'rxjs'
import { first } from 'rxjs/operators'
import { Branding } from '../../../../../React/Common/API/BrandingAPI'
import { useBranding } from '../../../../../React/Common/Hooks/useBranding'
import Drawer, { DrawerPosition } from '../../../../../React/Common/Components/Drawer'
import { EMPTY_STRING } from '../../../../../NonReact/constants/Common'
import { isStringNotEmpty, IsVarEmpty, isVarNotEmpty } from '../../../../../NonReact/utils/GlobalFunctions'
import { SidebarMenu } from './SidebarMenu'
import { ReactState } from '../../../../../React/Common/Types/Types'
import { IconButtonWithLabel } from '../../../Atoms/IconButtonWithLabel/IconButtonWithLabel'
import Icon, { IconName } from '../../../../../React/Common/Components/Icon'
import { usePersistentState } from '../../../../../React/Common/Hooks/usePersistState'
import { configureFullURLForLocalDevelopment } from '../../../../../React/Common/Utils/LocalUtils'
import { HeaderSidebar as HeaderSidebarLanguage } from '../../../../../../i18n'
import CLanguageHandler from '../../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext } from '../../../../../React/Common/Contexts'
import { useCurrentPage } from '../../../../Hooks/useCurrentPage'
import { useWaitForElements } from '../../../../../React/Common/Hooks/useWaitForElements'
import { ArrayUtils } from '../../../../../NonReact/utils/ArrayUtils'
import { ReportingMenu } from './ReportingMenu'
import { createDiv } from '../../../../../NonReact/DOM/DOMUtils'
import { RxJSCurrentState, useRxJSState } from '../../../../../React/Common/Hooks/useRxJSState'
import { mainMenuTrigger$ } from '../../../../../NonReact/common/RxJS/MainMenuTriggerState'
import { RouteID } from '../Types/Types'
import { UserAgents } from '../../../../../React/Common/Constants/UserAgents'
import { headerSidebarCollapse$ } from '../../../../../NonReact/common/RxJS/HeaderSidebarCollapseState'
import { $window } from '../../../../../NonReact/common/AkixiWindow'
import { SIDEBAR_PINNED_STORAGE_KEY } from '../../../../../NonReact/constants/ReactRouter/Header/Constants'
import { Page } from '../../../../../NonReact/constants/Components/Page'

type Props = {
    routeID?: Definable<RouteID>
}

const TEAMS_BODY_CLASS: string = 'teams-agent'
const HEADER_SIDEBAR_COLLAPSE: string = 'rs-header-sidebar-collapsed'

export const HeaderSidebar: FC<Props> = ({ routeID }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(HeaderSidebarLanguage.NAME)

    const currentPage: Definable<Page> = useCurrentPage()
    const mainPageContents: Nullable<HTMLElement[]> = useWaitForElements('.main-page-content')
    const [reportingMenuActive, setReportingMenuActive]: ReactState<boolean> = useState<boolean>(false)

    const teamsButtonContainer: MutableRefObject<Nullable<HTMLElement>> = useRef()

    const branding: Nullable<Branding> = useBranding()

    const isTeamsAgentEnabled: boolean = useMemo(
        (): boolean => $window?.AkixiUserAgentID === UserAgents.TEAMS,
        [$window?.AkixiUserAgentID]
    )

    const [isSidebarPinned, setIsSidebarPinned]: ReactState<boolean> = usePersistentState<boolean>(
        SIDEBAR_PINNED_STORAGE_KEY,
        false
    )

    const [isSidebarExpanded, setIsSidebarExpanded]: RxJSCurrentState<boolean> = useRxJSState<boolean>(
        mainMenuTrigger$,
        isSidebarPinned
    )

    const [isHeaderSidebarCollapsed]: RxJSCurrentState<boolean> = useRxJSState<boolean>(headerSidebarCollapse$, false)

    useHotkeys('ctrl+]', (): void => setIsSidebarExpanded(true))
    useHotkeys('ctrl+[', (): void => setIsSidebarExpanded(false))

    useEffect((): void => {
        document.body.classList.toggle('rs-sidebar-expanded', isSidebarExpanded)
    }, [isSidebarExpanded])

    useEffect((): void => {
        if (
            currentPage === Page.REPORTING &&
            isVarNotEmpty(mainPageContents) &&
            !ArrayUtils.isEmpty(mainPageContents) &&
            !reportingMenuActive
        ) {
            setReportingMenuActive(true)

            const mainPageContent: Nullable<HTMLElement> = mainPageContents[0]
            const reportingMenuContainer: Nullable<HTMLElement> = createDiv('reporting-menu-container')

            mainPageContent.classList.add('reporting-menu-enabled')
            mainPageContent.appendChild(reportingMenuContainer)

            render(<ReportingMenu />, reportingMenuContainer)
        }
    }, [currentPage, mainPageContents, reportingMenuActive])

    useEffect((): void => {
        if (isTeamsAgentEnabled) {
            document.body.classList.add(TEAMS_BODY_CLASS)

            if (IsVarEmpty(teamsButtonContainer.current)) {
                teamsButtonContainer.current = createDiv('teams-button-container')

                document.body.appendChild(teamsButtonContainer.current)

                render(
                    <ButtonBase
                        className='teams-header-sidebar-trigger'
                        onClick={(): Subscription =>
                            headerSidebarCollapse$.observable
                                .pipe(first())
                                .subscribe((collapsed: boolean): void => headerSidebarCollapse$.announce(!collapsed))
                        }
                    >
                        <span className='thst-icon'>
                            <Icon iconName={IconName.CHEVRON_LEFT} />
                        </span>
                    </ButtonBase>,
                    teamsButtonContainer.current
                )
            }
        } else {
            document.body.classList.remove(TEAMS_BODY_CLASS)
        }
    }, [isTeamsAgentEnabled])

    useEffect((): void => {
        document.body.classList.toggle(HEADER_SIDEBAR_COLLAPSE, isHeaderSidebarCollapsed)
        setIsSidebarExpanded(isHeaderSidebarCollapsed ? false : isSidebarPinned)
    }, [isHeaderSidebarCollapsed])

    return (
        <>
            <Drawer position={DrawerPosition.LEFT} isOpen={true} container={document.body}>
                <div>
                    {isStringNotEmpty(branding?.BrandingConfig?.imagery?.squareLogo) ? (
                        <>
                            <a
                                href={branding?.BrandingConfig?.links?.mainSiteLink?.url ?? EMPTY_STRING}
                                target={branding?.BrandingConfig?.links?.mainSiteLink?.target}
                                title={branding?.BrandingConfig?.links?.mainSiteLink?.title}
                                className='rs-main-logo'
                            >
                                {isSidebarExpanded ? (
                                    <img
                                        className='rs-full-logo'
                                        src={
                                            configureFullURLForLocalDevelopment(
                                                branding?.BrandingConfig?.imagery?.mainLogo
                                            ) ?? EMPTY_STRING
                                        }
                                        alt='Main Logo'
                                    />
                                ) : (
                                    <img
                                        className='rs-square-logo'
                                        src={branding?.BrandingConfig?.imagery?.squareLogo ?? EMPTY_STRING}
                                        alt='Square Logo'
                                    />
                                )}
                            </a>
                        </>
                    ) : (
                        <></>
                    )}
                    <hr className='rs-separator' />
                    <SidebarMenu routeID={routeID} isSidebarExpanded={!!isSidebarExpanded} />
                </div>
                <div>
                    <hr className='rs-separator' />
                    <div className='sidebar-bottom-container'>
                        <IconButtonWithLabel
                            title={
                                isSidebarExpanded
                                    ? language.get(HeaderSidebarLanguage.HeaderSidebar_CollapseMenu)
                                    : language.get(HeaderSidebarLanguage.HeaderSidebar_ExpandMenu)
                            }
                            iconName={isSidebarExpanded ? IconName.COLLAPSE_MENU : IconName.EXPAND_MENU}
                            onClick={(): void =>
                                setIsSidebarExpanded((sidebarExpanded: Definable<boolean>): boolean => !sidebarExpanded)
                            }
                        />
                        {isSidebarExpanded ? (
                            <IconButtonWithLabel
                                title={
                                    isSidebarPinned
                                        ? language.get(HeaderSidebarLanguage.HeaderSidebar_UnpinMenu)
                                        : language.get(HeaderSidebarLanguage.HeaderSidebar_PinMenu)
                                }
                                iconName={isSidebarPinned ? IconName.PINNED : IconName.PIN}
                                onClick={(): void =>
                                    setIsSidebarPinned((sidebarPinned: boolean): boolean => !sidebarPinned)
                                }
                            />
                        ) : (
                            <></>
                        )}
                    </div>
                </div>
            </Drawer>
        </>
    )
}
